'use es6';

import memoize from 'hs-lodash/memoize';
import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config as sanitizeConfig } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import * as Sync from 'ecosystem-marketplace-core/constants/dataSyncs/Sync';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as StorefrontCollection from '../constants/StorefrontCollection';
import * as SupportSource from 'ecosystem-marketplace-core/constants/search/SupportSource';
import * as SortField from '../constants/search/SortField';
import * as SortOrder from '../constants/search/SortOrder';
import * as FilterKey from '../constants/search/FilterKey';
import { getCategoryI18nKey, getSuperCategoryI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
export const getCollectionI18nKey = memoize((collection, textKey = 'titleText') => {
  const key = (() => {
    switch (collection) {
      case StorefrontCollection.POPULAR:
        return 'popular';

      case StorefrontCollection.NEW:
        return 'new';

      case StorefrontCollection.FREE:
        return 'free';

      case StorefrontCollection.HOMEMADE:
        return 'hubspot';

      default:
        {
          return collection;
        }
    }
  })();

  return `appMarketplace.storefront.filters.collection.items.${key}.${textKey}`;
}, (filter, textKey) => `${filter}:${textKey}`);
export const getPlanTypeFilterI18nKey = memoize(planType => {
  const key = (() => {
    switch (planType) {
      case PlanType.FREE:
        return 'free';

      case PlanType.MONTHLY:
        return 'monthly';

      case PlanType.YEARLY:
        return 'yearly';

      case PlanType.ONE_TIME:
        return 'oneTime';

      case PlanType.METERED:
        return 'metered';

      case PlanType.CONTACT_US:
        return 'contact_us';

      default:
        return null;
    }
  })();

  return `appMarketplace.storefront.filters.planType.items.${key}`;
});
export const getDataSyncFilterI18nKey = memoize(sync => {
  const key = (() => {
    switch (sync) {
      case Sync.INBOUND:
        return 'inbound';

      case Sync.OUTBOUND:
        return 'outbound';

      case Sync.BOTH:
        return 'both';

      default:
        return null;
    }
  })();

  return `appMarketplace.storefront.filters.sync.items.${key}`;
});
export const getSupportSourceFilterI18nKey = memoize(supportSource => {
  const key = (() => {
    switch (supportSource) {
      case SupportSource.HUBSPOT:
        return 'hubspot';

      case SupportSource.APP_PARTNER:
        return 'appPartner';

      default:
        return null;
    }
  })();

  return `appMarketplace.storefront.filters.supportSource.items.${key}`;
});
export const getFiltersHeadingI18nKey = (filters = {}) => {
  const {
    [FilterKey.COLLECTION]: collection,
    [FilterKey.SUPER_CATEGORY]: superCategory,
    [FilterKey.CATEGORY]: category
  } = filters;

  if (collection) {
    return getCollectionI18nKey(collection);
  } else if (category) {
    return getCategoryI18nKey(category);
  } else if (superCategory) {
    return getSuperCategoryI18nKey(superCategory);
  }

  return 'appMarketplace.storefront.results.resultsPage.headingText.allCategories';
}; // Copied from https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/setup/methods.js#L10-L32

const htmlEscapeMap = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '`': '&#x60;'
};
const htmlCharPattern = '(?:&|<|>|"|\'|`)';
const htmlTestRegexp = RegExp(htmlCharPattern);
const htmlReplaceRegexp = RegExp(htmlCharPattern, 'g');

const htmlReplacement = match => htmlEscapeMap[match];

const htmlEscape = value => {
  if (htmlTestRegexp.test(value)) {
    return value.replace(htmlReplaceRegexp, htmlReplacement);
  }

  return value;
}; // Inspired by https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/setup/methods.js#L819


export const formatParam = (optName, optValue) => {
  if (typeof optValue === 'number') {
    if (isNaN(optValue)) {
      return null;
    }

    if (optName === 'count') {
      return Number(optValue);
    }

    return I18n.formatNumber(optValue);
  } else if (optValue instanceof I18n.SafeString) {
    return optValue.toString();
  } else if (optValue != null) {
    return htmlEscape(String(optValue));
  }

  return optValue;
};
const placeholderRegex = /(?:\{\{)\s?(\S*?)\s?(?:\}\})/gm; // Inspired by https://git.hubteam.com/HubSpot/I18n/blob/b069f761220cd812dbd854d2a965f759672e2d91/static/js/i18n.js#L495

export const interpolate = (message, options) => {
  const modifiedOptions = {};

  for (const optName in options) {
    if (options.hasOwnProperty(optName)) {
      const optValue = options[optName];

      if (optValue != null) {
        modifiedOptions[optName] = formatParam(optName, optValue);
      }
    }
  }

  const matches = message.match(placeholderRegex);

  if (!matches) {
    return message;
  }

  let remainingMessage = message;
  let placeholder;
  let name;
  let regex;
  let value;

  while (matches.length) {
    placeholder = matches.shift();
    name = placeholder.replace(placeholderRegex, '$1');

    if (modifiedOptions[name] != null) {
      value = modifiedOptions[name].toString().replace(/\$/gm, '_#$#_');
    } else {
      // TODO report missing placeholder
      value = '';
    }

    regex = new RegExp(placeholder.replace(/\{/gm, '\\{').replace(/\}/gm, '\\}'));
    remainingMessage = remainingMessage.replace(regex, value);
  }

  return remainingMessage.replace(/_#\$#_/g, '$');
};
export const validText = (message, options, defaultValue = '', unescapeText = false) => {
  if (!isValidI18nKey(message)) {
    return sanitize(interpolate(defaultValue, options), sanitizeConfig.TEXTONLY);
  }

  return unescapeText ? unescapedText(message, options) : I18n.text(message, options);
};
export const validUnescapedText = (...args) => validText(...args, true);
export const getTranslatedSortText = (sortKey, sortOrder) => {
  let key = '';

  switch (sortKey) {
    case SortField.RELEVANCE:
      key = 'relevance';
      break;

    case SortField.HUB_INSTALLS:
      key = 'mostInstalled';
      break;

    case SortField.FIRST_PUBLISHED:
      key = 'newest';
      break;

    case SortField.PRICE:
      key = sortOrder === SortOrder.ASC ? 'priceLowHigh' : 'priceHighLow';
      break;

    case SortField.NAME:
      key = sortOrder === SortOrder.ASC ? 'name' : 'nameReverse';
      break;

    case null:
      key = 'mostInstalled';
      break;

    default:
      key = null;
      break;
  }

  if (key === null) {
    return null;
  }

  return I18n.text(`appMarketplace.storefront.results.sort.options.${key}`);
};